<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Settings2, X } from 'lucide-vue-next'

import BaseIcon from '@/components/BaseIcon.vue'
import { ACCENTS } from '@/data/accents'
import { useTweaks } from '@/composables/useTweaks'

const { t } = useI18n()
const { tweaks, setAccent } = useTweaks()

const open = ref(false)

const ACCENT_ENTRIES = Object.entries(ACCENTS)

function gradient(primary: string, end: string): string {
  return `linear-gradient(120deg, ${primary} 0%, ${end} 100%)`
}
</script>

<template>
  <div class="tweaks">
    <button class="tweaks-fab glass" type="button" :aria-label="$t('tweaks.customize')" @click="open = !open">
      <BaseIcon :icon="open ? X : Settings2" :size="18" />
    </button>

    <div v-show="open" class="tweaks-panel glass" role="dialog" :aria-label="$t('tweaks.title')">
      <div class="twk-label">{{ t('tweaks.accent') }}</div>
      <div class="twk-swatches">
        <button
          v-for="[key, preset] in ACCENT_ENTRIES"
          :key="key"
          class="twk-swatch"
          :class="{ active: tweaks.accent === key }"
          type="button"
          :style="{ background: gradient(preset.p, preset.g) }"
          :aria-label="preset.name"
          :title="preset.name"
          @click="setAccent(key)"
        ></button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tweaks-fab {
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 150;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: var(--fg-1);
  cursor: pointer;
  transition: transform 0.15s, border-color 0.2s;
}
.tweaks-fab:hover {
  border-color: var(--glass-brd-hi);
  transform: translateY(-2px);
}
.tweaks-fab:active {
  transform: scale(0.96);
}

.tweaks-panel {
  position: fixed;
  right: 22px;
  bottom: 80px;
  z-index: 150;
  width: 290px;
  border-radius: 18px;
  padding: 20px;
}
.twk-head {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 16px;
  letter-spacing: -0.01em;
}
.twk-section {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin: 18px 0 12px;
}
.twk-label {
  font-size: 13px;
  color: var(--fg-2);
  margin-bottom: 10px;
}
.twk-swatches {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 18px;
}
.twk-swatch {
  height: 34px;
  border-radius: 9px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.2s;
}
.twk-swatch:hover {
  transform: translateY(-2px);
}
.twk-swatch.active {
  border-color: var(--fg-1);
  box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.35);
}
.twk-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  border: none;
  color: var(--fg-1);
  font-family: var(--font-body);
  font-size: 14px;
  padding: 10px 0;
  cursor: pointer;
}
.twk-switch {
  width: 40px;
  height: 22px;
  border-radius: 999px;
  background: var(--track);
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
}
.twk-switch.on {
  background: var(--grad);
}
.twk-switch .knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
}
.twk-switch.on .knob {
  transform: translateX(18px);
}
</style>
